import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  Paper,
  Divider,
  Avatar
} from '@mui/material';
import { createTheme, ThemeProvider, useTheme, alpha } from '@mui/material/styles';
import {
  CalendarPlus,
  AlertCircle,
  CalendarCheck,
  Video,
  Briefcase,
  MessageCircle,
  CheckCircle,
  Copy
} from 'lucide-react';

import consultService from '../services/AGTUps-product-services/consultService';

/** 
 * Paleta de colores / theme 
 * similar a los otros shortcuts
 */
const COLORS = {
  primary: 'rgba(128, 128, 128, 0.5)',
  text: {
    light: '#FFFFFF',
    dark: '#FFFFFF'
  }
};

const createCustomTheme = (mode, actualMode) =>
  createTheme({
    palette: {
      mode,
      actualMode
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none'
            }
          },
          contained: {
            backgroundColor: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.primary,
            color: actualMode === 'wallpaper' ? '#000000' : '#ffffff',
            '&:hover': {
              backgroundColor: actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.85)' : COLORS.primary
            },
            '&:disabled': {
              backgroundColor:
                mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.12)'
                  : 'rgba(0, 0, 0, 0.12)'
            }
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: '16px',
            border: '1px solid',
            borderColor: actualMode === 'wallpaper'
              ? 'rgba(128, 128, 128, 0.3)'
              : (mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)'),
            backgroundColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.3)' : undefined,
            backdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
            WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none'
          }
        }
      }
    }
  });

// Expertos disponibles para seleccionar
const AVAILABLE_EXPERTS = [
  { id: 1, name: 'Estratega', role: 'Business Strategy', avatar: 'ES' },
  { id: 2, name: 'Analista', role: 'Market Analysis', avatar: 'AN' },
  { id: 3, name: 'Asesor', role: 'Financial Advisory', avatar: 'AS' },
  { id: 4, name: 'Innovador', role: 'Innovation Expert', avatar: 'IN' },
  { id: 5, name: 'Consultor', role: 'Business Consultant', avatar: 'CO' },
  { id: 6, name: 'Mentor', role: 'Growth Mentor', avatar: 'ME' }
];

// Tipos de reunión disponibles
const MEETING_TYPES = [
  { id: 'chat', name: 'Chat de Consulta', icon: MessageCircle, description: 'Consulta por chat con expertos' },
  { id: 'video', name: 'Videollamada', icon: Video, description: 'Reunión en vivo por video' },
  { id: 'business', name: 'Sesión Estratégica', icon: Briefcase, description: 'Análisis profundo con el equipo' }
];

// ======== Componente Shortcut: ScheduleMeetingShortcut ========
function ScheduleMeetingShortcut({ open, onClose }) {
  // Tomamos el modo del theme "padre"
  const parentTheme = useTheme();
  const customTheme = createCustomTheme(parentTheme.palette.mode, parentTheme.palette.actualMode);
  const isDark = parentTheme.palette.mode === 'dark';

  // Estados para el formulario
  const [sessionTitle, setSessionTitle] = useState('');
  const [sessionDate, setSessionDate] = useState('');
  const [sessionTime, setSessionTime] = useState('');
  const [selectedExperts, setSelectedExperts] = useState([]);
  const [sessionNotes, setSessionNotes] = useState('');
  const [meetingType, setMeetingType] = useState('chat');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [scheduledSession, setScheduledSession] = useState(null);
  
  // Estado para notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Inicializar valores al abrir el shortcut
  useEffect(() => {
    if (open) {
      // Establecer fecha para mañana a las 12 PM por defecto
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(12, 0, 0, 0);
      
      // Formatear fecha para el input type="date"
      const formattedDate = tomorrow.toISOString().split('T')[0];
      
      // Formatear hora para el input type="time"
      const formattedTime = `${tomorrow.getHours().toString().padStart(2, '0')}:${tomorrow.getMinutes().toString().padStart(2, '0')}`;
      
      setSessionDate(formattedDate);
      setSessionTime(formattedTime);
      setSessionTitle('Consulta estratégica');
      setSelectedExperts(AVAILABLE_EXPERTS.slice(0, 3));
      setSessionNotes('');
      setMeetingType('chat');
      setSuccess(false);
      setScheduledSession(null);
    }
  }, [open]);

  // Mostrar snackbar
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Cerrar snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  // Validar formulario
  const validateForm = () => {
    if (!sessionTitle.trim()) {
      showSnackbar('Por favor ingresa un título para la sesión', 'error');
      return false;
    }
    
    if (!sessionDate) {
      showSnackbar('Por favor selecciona una fecha', 'error');
      return false;
    }
    
    if (!sessionTime) {
      showSnackbar('Por favor selecciona una hora', 'error');
      return false;
    }
    
    if (selectedExperts.length === 0) {
      showSnackbar('Selecciona al menos un experto para la sesión', 'error');
      return false;
    }
    
    // Validar que la fecha sea futura
    const selectedDateTime = new Date(`${sessionDate}T${sessionTime}`);
    if (selectedDateTime < new Date()) {
      showSnackbar('La fecha de la sesión debe ser en el futuro', 'error');
      return false;
    }
    
    return true;
  };

  // Programar reunión
  const handleScheduleMeeting = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const selectedDateTime = new Date(`${sessionDate}T${sessionTime}`);
      
      // Simular ID para esta demo
      const consultationId = `session-${Date.now()}`;
      
      const sessionData = {
        consultationId,
        title: sessionTitle,
        expertPanel: selectedExperts,
        scheduledTime: selectedDateTime.toISOString(),
        notes: sessionNotes,
        meetingType: meetingType
      };
      
      // Llamar al servicio para agendar
      const res = await consultService.scheduleMeeting(consultationId, selectedDateTime.toISOString());
      
      if (res.success) {
        showSnackbar('Reunión programada exitosamente');
        setSuccess(true);
        setScheduledSession({
          ...sessionData,
          status: 'programado',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          meetingId: `meet-${Math.floor(Math.random() * 1000000)}`,
          meetingUrl: 'https://meet.agtcouncil.com/' + Math.random().toString(36).substring(2, 10)
        });
      }
    } catch (error) {
      console.error('Error scheduling meeting:', error);
      showSnackbar('Error al programar la reunión', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Formatear fecha para mostrar
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Formatear hora para mostrar
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Renderizar pantalla de éxito
  const renderSuccessScreen = () => {
    if (!scheduledSession) return null;
    
    const selectedDateTime = new Date(`${sessionDate}T${sessionTime}`);
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 2 }}>
        <Box 
          sx={{ 
            width: 60, 
            height: 60, 
            borderRadius: '50%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: isDark ? alpha('#31C859', 0.2) : alpha('#31C859', 0.1)
          }}
        >
          <CheckCircle size={30} color="#31C859" />
        </Box>
        
        <Typography variant="h5" fontWeight={600} textAlign="center">
          ¡Reunión programada correctamente!
        </Typography>
        
        <Paper 
          elevation={0}
          sx={{ 
            p: 3, 
            width: '100%', 
            borderRadius: 2,
            bgcolor: isDark ? alpha(COLORS.primary, 0.1) : alpha(COLORS.primary, 0.05),
            border: `1px solid ${isDark ? alpha(COLORS.primary, 0.2) : alpha(COLORS.primary, 0.1)}`
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Detalles de la reunión
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Título
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {scheduledSession.title}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Tipo de reunión
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {MEETING_TYPES.find(t => t.id === scheduledSession.meetingType)?.name || 'Chat de Consulta'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Fecha
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {formatDate(selectedDateTime)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Hora
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {formatTime(selectedDateTime)}
              </Typography>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" gutterBottom>
            Expertos
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {scheduledSession.expertPanel.map((expert, index) => (
              <Chip
                key={index}
                avatar={
                  <Avatar sx={{
                    bgcolor: index === 0 ? 'rgba(128, 128, 128, 0.6)' : index === 1 ? 'rgba(128, 128, 128, 0.7)' : 'rgba(128, 128, 128, 0.65)'
                  }}>
                    {expert.name?.substring(0, 2).toUpperCase() || 'Ex'}
                  </Avatar>
                }
                label={`${expert.name} - ${expert.role}`}
                variant="outlined"
                size="small"
              />
            ))}
          </Box>
          
          {scheduledSession.meetingType === 'video' && (
            <>
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom>
                Enlace de conexión
              </Typography>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  p: 1.5,
                  bgcolor: isDark ? alpha('#000', 0.2) : alpha('#000', 0.05),
                  borderRadius: 1,
                  gap: 1
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontFamily: 'monospace',
                    flexGrow: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {scheduledSession.meetingUrl}
                </Typography>
                <Tooltip title="Copiar enlace">
                  <IconButton size="small">
                    <Copy size={16} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Este enlace será enviado a todos los participantes por correo electrónico.
              </Typography>
            </>
          )}
          
          {scheduledSession.notes && (
            <>
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom>
                Notas
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                {scheduledSession.notes}
              </Typography>
            </>
          )}
        </Paper>
        
        <Alert severity="info" sx={{ width: '100%' }}>
          <Typography variant="body2">
            Se ha enviado una confirmación a tu correo con los detalles de la reunión.
            Recibirás un recordatorio 30 minutos antes del inicio.
          </Typography>
        </Alert>
      </Box>
    );
  };

  // Renderizar el formulario
  const renderForm = () => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Información de la reunión
        </Typography>
        
        <TextField
          label="Título de la reunión"
          fullWidth
          value={sessionTitle}
          onChange={(e) => setSessionTitle(e.target.value)}
          margin="dense"
          placeholder="Ej: Consulta estratégica, Análisis financiero, etc."
        />
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Tipo de reunión
          </Typography>
          <Grid container spacing={2}>
            {MEETING_TYPES.map((type) => (
              <Grid item xs={12} sm={4} key={type.id}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${meetingType === type.id 
                      ? COLORS.primary 
                      : isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
                    bgcolor: meetingType === type.id 
                      ? (isDark ? alpha(COLORS.primary, 0.15) : alpha(COLORS.primary, 0.05))
                      : 'transparent',
                    cursor: 'pointer',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: COLORS.primary,
                      bgcolor: isDark ? alpha(COLORS.primary, 0.1) : alpha(COLORS.primary, 0.03),
                    }
                  }}
                  onClick={() => setMeetingType(type.id)}
                >
                  <Box 
                    sx={{ 
                      p: 1.5, 
                      borderRadius: '50%', 
                      bgcolor: isDark ? alpha(COLORS.primary, 0.15) : alpha(COLORS.primary, 0.1) 
                    }}
                  >
                    <type.icon size={24} color={COLORS.primary} />
                  </Box>
                  <Typography variant="subtitle2" fontWeight={600} textAlign="center">
                    {type.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" textAlign="center">
                    {type.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Fecha"
              type="date"
              value={sessionDate}
              onChange={(e) => setSessionDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              margin="dense"
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Hora"
              type="time"
              value={sessionTime}
              onChange={(e) => setSessionTime(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              margin="dense"
              inputProps={{ step: 900 }}
            />
          </Grid>
        </Grid>
        
        <FormControl fullWidth margin="dense">
          <InputLabel id="experts-select-label">Expertos</InputLabel>
          <Select
            labelId="experts-select-label"
            multiple
            value={selectedExperts}
            onChange={(e) => setSelectedExperts(e.target.value)}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((expert) => (
                  <Chip 
                    key={expert.id} 
                    label={expert.name} 
                    size="small" 
                  />
                ))}
              </Box>
            )}
          >
            {AVAILABLE_EXPERTS.map((expert) => (
              <MenuItem key={expert.id} value={expert}>
                {expert.name} - {expert.role}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <TextField
          label="Notas para la reunión"
          multiline
          rows={3}
          fullWidth
          value={sessionNotes}
          onChange={(e) => setSessionNotes(e.target.value)}
          margin="dense"
          placeholder="Describe brevemente el objetivo de esta reunión..."
        />
        
        <Box sx={{ mt: 2, p: 2, bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)', borderRadius: 1 }}>
          <Box display="flex" alignItems="flex-start" gap={1}>
            <AlertCircle size={20} style={{ color: isDark ? 'rgba(128, 128, 128, 0.7)' : 'rgba(128, 128, 128, 0.7)', marginTop: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Recuerda que dispones de una consulta diaria con duración máxima de 1 hora. 
              La sesión agendada reservará tu tiempo para que puedas consultar con los expertos seleccionados
              en la fecha y hora programadas.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        aria-labelledby="schedule-meeting-dialog"
      >
        {/* Title + Actions */}
        <DialogTitle
          id="schedule-meeting-dialog"
          sx={{ display: 'flex', justifyContent: 'space-between', pb: 1 }}
        >
          <Box display="flex" alignItems="center">
            <CalendarPlus size={20} style={{ marginRight: 8 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {success ? 'Reunión Programada' : 'Programar Reunión'}
            </Typography>
          </Box>
        </DialogTitle>

        {/* Contenido Principal */}
        <DialogContent dividers sx={{ minHeight: '450px' }}>
          {success ? renderSuccessScreen() : renderForm()}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              color: parentTheme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : undefined,
              borderColor: parentTheme.palette.actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.3)' : undefined
            }}
          >
            {success ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!success && (
            <Button 
              onClick={handleScheduleMeeting} 
              variant="contained" 
              color="primary"
              startIcon={loading ? <CircularProgress size={16} /> : <CalendarCheck size={16} />}
              disabled={loading}
            >
              {loading ? 'Programando...' : 'Programar Reunión'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

export default ScheduleMeetingShortcut;